import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  Modal,
} from "@material-ui/core";
import {
  CustomCheckbox,
  CheckBoxLabel,
  CheckboxContainer,
} from "../../FormInputs";
import { WhatsApp, Close } from "@material-ui/icons";
import LockIcon from "@material-ui/icons/Lock";
import GroupIcon from "@material-ui/icons/Group";
import HttpsLockOpen from "@material-ui/icons/LockOpen";
import PublicIcon from "@material-ui/icons/Public";
import DescriptionIcon from "@material-ui/icons/Description";
import theme from "../../../../assets/theme";
import COLORS from "../../../../assets/Colors";
import { PrimaryCTAButton, PrimaryOutlinedCloseCTAButton } from "../../Buttons";
import { hasRedactions } from "./redaction";

const WHATSAPP_RECIPIENT_OPTIONS_BY_VISIBILITY = {
  all: [
    { label: "Claimant", value: "claimant" },
    { label: "Respondent", value: "respondent" },
    { label: "Neutrals", value: "neutrals" },
    { label: "Case Manager", value: "caseManager" },
    { label: "Co Case Manager", value: "coCaseManager" },
    { label: "All", value: "all" },
  ],
  others: [
    { label: "Neutrals", value: "neutrals" },
    { label: "Case Manager", value: "caseManager" },
    { label: "Co Case Manager", value: "coCaseManager" },
    { label: "All", value: "all" },
  ],
  me: [
    { label: "Case Manager", value: "caseManager" },
    { label: "Co Case Manager", value: "coCaseManager" },
    { label: "All", value: "all" },
  ],
};

const VISIBILITY_CONFIG = {
  all: {
    label: "Share with all parties",
    Icon: PublicIcon,
    color: "#0369a1",
    bg: "#f0f9ff",
    border: "#bae6fd",
  },
  others: {
    label: "Share with Case Manager & Neutrals",
    Icon: HttpsLockOpen,
    color: "#b45309",
    bg: "#fffbeb",
    border: "#fde68a",
  },
  me: {
    label: "Share With My Team",
    Icon: GroupIcon,
    color: "#6d28d9",
    bg: "#f5f3ff",
    border: "#ddd6fe",
  },
  strictMode: {
    label: "Private",
    Icon: LockIcon,
    color: "#dc2626",
    bg: "#fef2f2",
    border: "#fecaca",
  },
};

const getVisibilityKey = (type) => {
  if (type === "me") return "me";
  if (type === "others") return "others";
  if (type === "strictMode") return "strictMode";
  return "all";
};

export default function WhatsAppDocNotificationModal({
  open,
  onClose,
  doc,
  onSend = () => {},
}) {
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [shareVersion, setShareVersion] = useState("original");

  const docUrl = doc?.url?.url;
  const isPdf = /\.pdf$/i.test((docUrl || "").split("?")[0]);
  const docHasRedactions = isPdf && hasRedactions(docUrl);

  useEffect(() => {
    if (open) {
      setSelectedRecipients([]);
      setShareVersion(docHasRedactions ? "redacted" : "original");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc]);

  const visibilityKey = getVisibilityKey(doc?.visibilityType);
  const visMeta = VISIBILITY_CONFIG[visibilityKey] || VISIBILITY_CONFIG.all;
  const recipientOptions =
    WHATSAPP_RECIPIENT_OPTIONS_BY_VISIBILITY[visibilityKey] || [];

  const individualRecipientValues = recipientOptions
    .map((o) => o.value)
    .filter((v) => v !== "all");

  const isAllSelected =
    individualRecipientValues.length > 0 &&
    individualRecipientValues.every((val) => selectedRecipients.includes(val));

  const handleToggleRecipient = (recipientValue) => {
    if (recipientValue === "all") {
      if (isAllSelected) {
        setSelectedRecipients([]);
      } else {
        setSelectedRecipients([...individualRecipientValues]);
      }
    } else {
      if (selectedRecipients.includes(recipientValue)) {
        setSelectedRecipients(
          selectedRecipients.filter((v) => v !== recipientValue),
        );
      } else {
        setSelectedRecipients([...selectedRecipients, recipientValue]);
      }
    }
  };

  const handleSend = (e) => {
    e.preventDefault();
    if (selectedRecipients.length === 0) return;
    onSend({
      documentId: doc?.id,
      documentName: doc?.name,
      documentUrl: docUrl,
      visibilityType: doc?.visibilityType,
      recipients: selectedRecipients,
      redacted: docHasRedactions && shareVersion === "redacted",
    });
  };

  return (
    <Modal
      open={Boolean(open)}
      onClose={onClose}
      disableRestoreFocus={true}
    >
      <DrawerContainer role="presentation">
        <Container>
          <HeadingContainer>
            <Heading>
              <WhatsAppBadge>
                <WhatsApp style={{ fontSize: 18, color: "#ffffff" }} />
              </WhatsAppBadge>
              Send WhatsApp Notification
            </Heading>
            <CloseButton onClick={onClose} type="button" aria-label="Close">
              <Close style={{ fontSize: 20, color: "#64748b" }} />
            </CloseButton>
          </HeadingContainer>

          <BodyContainer>
            <DocCard>
              <DocCardLeft>
                <DocIconBadge>
                  <DescriptionIcon style={{ fontSize: 20, color: "#00838c" }} />
                </DocIconBadge>
                <DocTextGroup>
                  <DocName title={doc?.name}>
                    {doc?.name || "Document"}
                  </DocName>
                  <DocSub>
                    {doc?.documentNo ? `Ref: ${doc.documentNo}` : "Core Bundle Document"}
                    {doc?.formattedDate ? ` • ${doc.formattedDate}` : ""}
                  </DocSub>
                </DocTextGroup>
              </DocCardLeft>
              <VisibilityBadge
                $color={visMeta.color}
                $bg={visMeta.bg}
                $border={visMeta.border}
              >
                <visMeta.Icon style={{ fontSize: 13, color: visMeta.color, marginRight: 5 }} />
                <span>{visMeta.label}</span>
              </VisibilityBadge>
            </DocCard>

            {isPdf && (
              <>
                <SectionHeading>Document to share</SectionHeading>
                <VersionOptions>
                  <VersionOption
                    type="button"
                    $active={!docHasRedactions || shareVersion === "original"}
                    onClick={() => setShareVersion("original")}
                  >
                    <VersionTitle>Original</VersionTitle>
                    <VersionSub>Full document, unchanged</VersionSub>
                  </VersionOption>
                  <VersionOption
                    type="button"
                    $active={docHasRedactions && shareVersion === "redacted"}
                    disabled={!docHasRedactions}
                    onClick={() => setShareVersion("redacted")}
                  >
                    <VersionTitle>Redacted</VersionTitle>
                    <VersionSub>
                      {docHasRedactions
                        ? "Marked text blacked out"
                        : "No redactions yet. Open the document and use Redact."}
                    </VersionSub>
                  </VersionOption>
                </VersionOptions>
              </>
            )}

            <SectionHeading>WhatsApp Notification With Attachment</SectionHeading>
            <SectionSub>
              Select the recipients who will receive this document via WhatsApp:
            </SectionSub>

            <RecipientOptions>
              {recipientOptions.map((recipient) => {
                const isChecked =
                  recipient.value === "all"
                    ? isAllSelected
                    : selectedRecipients.includes(recipient.value);

                return (
                  <CheckboxContainer key={recipient.value}>
                    <CustomCheckbox
                      id={`wa-modal-${recipient.value}`}
                      inputProps={{ id: `wa-modal-${recipient.value}` }}
                      checked={isChecked}
                      onChange={() => handleToggleRecipient(recipient.value)}
                    />
                    <CheckBoxLabel htmlFor={`wa-modal-${recipient.value}`}>
                      {recipient.label}
                    </CheckBoxLabel>
                  </CheckboxContainer>
                );
              })}
            </RecipientOptions>

            <ButtonContainer>
              <PrimaryOutlinedCloseCTAButton
                style={{ width: "45%" }}
                onClick={onClose}
              >
                Cancel
              </PrimaryOutlinedCloseCTAButton>
              <PrimaryCTAButton
                style={{
                  width: "45%",
                  backgroundColor:
                    selectedRecipients.length === 0 ? undefined : "#10b981",
                  borderColor:
                    selectedRecipients.length === 0 ? undefined : "#10b981",
                }}
                disabled={selectedRecipients.length === 0}
                onClick={handleSend}
              >
                Send Notification
              </PrimaryCTAButton>
            </ButtonContainer>
          </BodyContainer>
        </Container>
      </DrawerContainer>
    </Modal>
  );
}

const DrawerContainer = styled.div`
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 480px;
  outline: none;
`;

const Container = styled.div`
  margin: 0 16px;
  background-color: #ffffff;
  outline: none;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  border: 1px solid #e2e8f0;
  @media ${theme?.breakpoints?.sm_up} {
    margin: 0;
  }
`;

const HeadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  background: #ffffff;
`;

const Heading = styled.h2`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 16px;
  color: ${COLORS.COLOR_DARK};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const WhatsAppBadge = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #25d366;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background 0.15s;
  &:hover {
    background: #f1f5f9;
  }
`;

const BodyContainer = styled.div`
  padding: 18px 20px 20px;
`;

const DocCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const DocCardLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

const DocIconBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: #e6fffa;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const DocTextGroup = styled.div`
  min-width: 0;
  flex: 1;
`;

const DocName = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: ${COLORS.COLOR_DARK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DocSub = styled.div`
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
`;

const VisibilityBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  border: 1px solid ${({ $border }) => $border};
  flex-shrink: 0;
`;

const SectionHeading = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: ${COLORS.COLOR_DARK};
  margin-bottom: 2px;
`;

const SectionSub = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-bottom: 8px;
`;

const RecipientOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin: 8px 0 16px;

  .MuiFormControlLabel-root {
    margin-right: 0;
  }

  .MuiFormControlLabel-label {
    color: ${COLORS.COLOR_DARK};
    font-family: ${theme.fonts.primaryFontSemiBold};
    font-size: 13px;
  }
`;

const VersionOptions = styled.div`
  display: flex;
  gap: 10px;
  margin: 8px 0 16px;
`;

const VersionOption = styled.button`
  flex: 1;
  text-align: left;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1.5px solid ${({ $active }) => ($active ? "#00838c" : "#e2e8f0")};
  background: ${({ $active }) => ($active ? "#f0fafb" : "#ffffff")};
  cursor: pointer;
  font-family: ${theme.fonts.primaryFontRegular};
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const VersionTitle = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: ${COLORS.COLOR_DARK};
`;

const VersionSub = styled.div`
  font-size: 11.5px;
  color: #64748b;
  margin-top: 2px;
  line-height: 1.4;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #f1f5f9;
`;
